import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from 'react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return;
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)');
}

export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/** Rotates through a list of lines. The whole develop screen hangs off this. */
export function useRotatingLine(lines: readonly string[], intervalMs = 2600): string {
  const [index, setIndex] = useState(0);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (lines.length <= 1) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % lines.length), reduced ? intervalMs * 2 : intervalMs);
    return () => clearInterval(id);
  }, [lines, intervalMs, reduced]);

  return lines[index % lines.length] ?? '';
}

export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function useCopyToClipboard(resetMs = 1800): [boolean, (text: string) => Promise<boolean>] {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const copy = useCallback(
    async (text: string) => {
      try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
        else throw new Error('no clipboard');
        setCopied(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), resetMs);
        return true;
      } catch {
        return false;
      }
    },
    [resetMs],
  );

  return [copied, copy];
}

/** Sentinel-based infinite scroll. Returns a ref to put at the end of a list. */
export function useInfiniteScroll(
  onLoadMore: () => void,
  options: { enabled?: boolean; rootMargin?: string } = {},
) {
  const { enabled = true, rootMargin = '600px' } = options;
  const sentinel = useRef<HTMLDivElement | null>(null);
  const callback = useRef(onLoadMore);
  callback.current = onLoadMore;

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !enabled || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) callback.current();
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, rootMargin]);

  return sentinel;
}

/**
 * How many sheets/modals currently want the page not to scroll.
 *
 * Counted rather than saved-and-restored: LookDetail holds five independent
 * `open` flags, and two overlapping locks each captured `previous === 'hidden'`,
 * so the second unlock restored `hidden` and the page stayed frozen.
 */
let scrollLocks = 0;
let scrollLockPrevious = '';

/** Locks body scroll while a sheet or modal owns the screen. */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    if (scrollLocks === 0) {
      scrollLockPrevious = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    scrollLocks += 1;
    return () => {
      scrollLocks -= 1;
      if (scrollLocks === 0) document.body.style.overflow = scrollLockPrevious;
    };
  }, [active]);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The four things an `aria-modal` dialog owes a keyboard user: focus moves in,
 * Tab cannot leave, Escape closes, focus returns to whatever opened it.
 *
 * `aria-modal="true"` hides the rest of the page from assistive tech, so a
 * dialog that skips this does not merely inconvenience a screen-reader user —
 * it parks their focus on content the AT has been told does not exist.
 */
export function useDialogFocus(
  active: boolean,
  panelRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  useEffect(() => {
    if (!active) return;
    const restore = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    (panel?.querySelector<HTMLElement>(FOCUSABLE) ?? panel)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (node) => node.offsetParent !== null || node === document.activeElement,
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      restore?.focus?.();
    };
  }, [active, panelRef, onClose]);
}

/** Roving-tabindex keyboard nav for horizontal rails (mood rail, hype bar). */
export function useRovingIndex(count: number, initial = 0) {
  const [active, setActive] = useState(initial);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (count === 0) return;
      let next = active;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (active + 1) % count;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (active - 1 + count) % count;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = count - 1;
      else return;
      event.preventDefault();
      setActive(next);
    },
    [active, count],
  );

  return { active, setActive, onKeyDown };
}

export function useInterval(callback: () => void, delayMs: number | null): void {
  const saved = useRef(callback);
  saved.current = callback;
  useEffect(() => {
    if (delayMs === null) return;
    const id = setInterval(() => saved.current(), delayMs);
    return () => clearInterval(id);
  }, [delayMs]);
}
